import {
  AppShellContent,
  Button,
  Group,
  Headline,
  Stack,
} from "@assetwatch/ui";
import { useMemo, useState } from "react";
import { DocumentDetailDrawer } from "../components/snowflakes/documents/DocumentDetailDrawer";
import { DocumentsList } from "../components/snowflakes/documents/DocumentsList";
import { DocumentsToolbar } from "../components/snowflakes/documents/DocumentsToolbar";
import { UploadDocumentModal } from "../components/snowflakes/documents/UploadDocumentModal";
import { useWorkScope } from "../components/WorkScopeProvider";
import { useListDocuments } from "../queries/documents";

export function DocumentsPage() {
  const [selectedDocumentId, setSelectedDocumentId] = useState<number | null>(
    null,
  );
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState<
    "Published" | "Unpublished" | "All"
  >("All");
  const [categoryFilters, setCategoryFilters] = useState<number[]>([]);
  const [currentPage, setCurrentPage] = useState(0);

  const {
    selectedFacility,
    selectedCustomer,
    isPending: scopePending,
  } = useWorkScope();

  // Get facility ID (it's a UUID string, not a numeric ID)
  const facilityId = selectedFacility?.id || "";

  const { data, error } = useListDocuments({
    customerId: selectedCustomer?.id || "",
    facilityId,
    search: searchText,
    status: statusFilter === "All" ? undefined : statusFilter,
    categoryIds: categoryFilters.length > 0 ? categoryFilters : undefined,
    offset: currentPage * 50,
    limit: 50,
  });

  const totalPages = useMemo(() => {
    return Math.ceil((data?.total || 0) / 50);
  }, [data?.total]);

  const handleReset = () => {
    setSearchText("");
    setStatusFilter("All");
    setCategoryFilters([]);
    setCurrentPage(0);
  };

  if ((!selectedFacility || !facilityId) && !scopePending) {
    return (
      <AppShellContent>
        <Stack gap={16}>
          <Headline as="h1" size="lg">
            Documents
          </Headline>
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            <p className="text-sm text-yellow-800">
              Please select a facility to view documents.
            </p>
          </div>
        </Stack>
      </AppShellContent>
    );
  }

  if (error) {
    const errorMessage = String(error);
    const is403 = errorMessage.includes("403");
    return (
      <AppShellContent>
        <Stack gap={16}>
          <Headline as="h1" size="lg">
            Documents
          </Headline>
          <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
            {is403 ? (
              <p className="text-sm text-yellow-800">
                You don't have permission to view documents at this facility.
              </p>
            ) : (
              <p className="text-sm text-red-800">
                Error loading documents: {errorMessage}
              </p>
            )}
          </div>
        </Stack>
      </AppShellContent>
    );
  }

  return (
    <AppShellContent>
      <Stack gap={24}>
        {/* Header */}
        <Group justify="between" wrap="wrap">
          <Headline as="h1" size="lg">
            Documents
          </Headline>
          <Button
            icon="plus-regular"
            onClick={() => setShowUploadModal(true)}
            size="sm"
          >
            Upload document
          </Button>
        </Group>

        {/* Toolbar */}
        <DocumentsToolbar
          searchText={searchText}
          onSearchChange={setSearchText}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          categoryFilters={categoryFilters}
          onCategoryFiltersChange={setCategoryFilters}
          onReset={handleReset}
        />

        {/* Documents List */}
        <DocumentsList
          rows={data?.rows || []}
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
          onRowClick={setSelectedDocumentId}
        />
      </Stack>

      {/* Detail Drawer */}
      {selectedDocumentId && selectedFacility && (
        <DocumentDetailDrawer
          documentId={selectedDocumentId}
          customerId={selectedCustomer?.id || ""}
          facilityId={facilityId}
          isOpen={!!selectedDocumentId}
          onClose={() => setSelectedDocumentId(null)}
        />
      )}

      {/* Upload Modal */}
      {showUploadModal && selectedFacility && (
        <UploadDocumentModal
          customerId={selectedCustomer?.id || ""}
          facilityId={facilityId}
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          onDocumentCreated={() => {
            setShowUploadModal(false);
            setCurrentPage(0);
          }}
        />
      )}
    </AppShellContent>
  );
}
